import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const TimelineItem = ({ exp, index, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      variants={reveal}
      className="relative grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 mb-8 last:mb-0"
      data-testid="experience-timeline-item"
    >
      {/* Left side - Dates (desktop) */}
      <div className={`hidden lg:flex lg:col-span-5 ${
        index % 2 === 0 ? 'justify-end' : 'order-last justify-start'
      } items-start pt-2`}>
        <div className={`text-right ${index % 2 !== 0 ? 'text-left' : ''}`}>
          <span className="mono-label text-muted-foreground">{exp.dates}</span>
          {exp.location && (
            <span className="block text-xs text-muted-foreground mt-1">{exp.location}</span>
          )}
        </div>
      </div>

      {/* Center dot */}
      <div className="hidden lg:flex lg:col-span-2 justify-center relative">
        <div className={`w-4 h-4 rounded-full border-2 mt-2 z-10 ${
          exp.current
            ? 'border-primary bg-primary shadow-[0_0_12px_rgba(233,30,99,0.5)]'
            : 'border-accent bg-accent/30'
        }`} />
      </div>

      {/* Mobile dot + date */}
      <div className="flex lg:hidden items-center gap-3 mb-2">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
          exp.current
            ? 'bg-primary shadow-[0_0_12px_rgba(233,30,99,0.5)]'
            : 'bg-accent/50'
        }`} />
        <span className="mono-label text-muted-foreground">{exp.dates}</span>
        {exp.current && (
          <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">
            Current
          </Badge>
        )}
      </div>

      {/* Content card */}
      <div className={`lg:col-span-5 ${index % 2 === 0 ? '' : 'lg:order-first'}`}>
        <Card
          className={`p-5 bg-card border-border glow-card cursor-pointer select-none`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base" style={{ fontFamily: 'var(--font-display)' }}>
                  {exp.title}
                </h3>
                {exp.current && (
                  <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px] hidden lg:inline-flex">
                    Current
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{exp.company}</p>
              {exp.department && (
                <p className="text-xs text-muted-foreground mt-0.5">{exp.department}</p>
              )}
            </div>
            <div className="text-muted-foreground flex-shrink-0 mt-1">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>

          {expanded && exp.bullets && exp.bullets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 pt-4 border-t border-border"
            >
              <ul className="space-y-2">
                {exp.bullets.map((bullet, bi) => (
                  <li key={bi} className="text-sm text-muted-foreground flex gap-2">
                    <span className="text-primary mt-1.5 flex-shrink-0">&#8226;</span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </Card>
      </div>
    </motion.div>
  );
};

export const ExperienceSection = ({ experience }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="experience"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="experience-section"
      aria-label="Work Experience and Career Timeline"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <span className="mono-label text-primary mb-3 block">Career</span>
            <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Experience & Timeline
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto">
              A journey through law enforcement, technology, management, and entrepreneurship.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline center line (desktop) */}
            <div className="timeline-line" />

            {experience.map((exp, i) => (
              <TimelineItem
                key={`${exp.company}-${exp.title}`}
                exp={exp}
                index={i}
                isLast={i === experience.length - 1}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
